import { api } from '../../client';

const API_BASE_URL = api.baseURL;

function buildQuery(params: Record<string, string>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') sp.set(k, v);
  });
  const q = sp.toString();
  return q ? `?${q}` : '';
}

/** Report endpoint path by key (without base URL). */
export const REPORT_ENDPOINTS: Record<string, string> = {
  'financial/profit-loss': '/reports/financial/profit-loss',
  'financial/balance-sheet': '/reports/financial/balance-sheet',
  'financial/cash-flow': '/reports/financial/cash-flow',
  'financial/trial-balance': '/reports/financial/trial-balance',
  'financial/general-ledger': '/reports/financial/general-ledger',
  'ar/aging-summary': '/reports/ar/aging-summary',
  'ar/customer-statements': '/reports/ar/customer-statements',
  'ar/sales-by-customer': '/reports/ar/sales-by-customer',
  'ar/invoice-register': '/reports/ar/invoice-register',
  'ap/aging-summary': '/reports/ap/aging-summary',
  'ap/vendor-statements': '/reports/ap/vendor-statements',
  'ap/purchases-by-vendor': '/reports/ap/purchases-by-vendor',
  'ap/bill-register': '/reports/ap/bill-register',
  'inventory/valuation': '/reports/inventory/valuation',
  'inventory/stock-status': '/reports/inventory/stock-status',
  'inventory/movement': '/reports/inventory/movement',
  'hr/employee-directory': '/reports/hr/employee-directory',
  'hr/payroll-summary': '/reports/hr/payroll-summary',
  'hr/department-headcount': '/reports/hr/department-headcount',
};

export type ReportKey = keyof typeof REPORT_ENDPOINTS;

export const reportsApi = {
  /**
   * Run a report and return JSON data (no export).
   */
  async get(
    reportKey: ReportKey,
    params: { from_date?: string; to_date?: string; account_id?: string }
  ): Promise<{ data?: unknown[]; message?: string }> {
    const path = REPORT_ENDPOINTS[reportKey];
    if (!path) {
      return Promise.reject(new Error(`Unknown report: ${reportKey}`));
    }
    const q = buildQuery({
      from_date: params.from_date ?? '',
      to_date: params.to_date ?? '',
      account_id: params.account_id ?? '',
    });
    return api.get(`${path}${q}`) as Promise<{ data: unknown[]; message?: string }>;
  },

  /**
   * Export report as CSV for the given date range. Triggers browser download.
   */
  async exportCsv(
    reportKey: ReportKey,
    params: { from_date: string; to_date: string; account_id?: string }
  ): Promise<void> {
    const path = REPORT_ENDPOINTS[reportKey];
    if (!path) {
      throw new Error(`Unknown report: ${reportKey}`);
    }
    const query = new URLSearchParams({
      from_date: params.from_date,
      to_date: params.to_date,
      format: 'csv',
    });
    if (params.account_id) query.set('account_id', params.account_id);
    const url = `${API_BASE_URL}${path}${query.toString() ? `?${query.toString()}` : ''}`;
    const token = localStorage.getItem('auth_token');
    const response = await fetch(url, {
      headers: {
        Accept: 'text/csv',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Export failed: ${response.status}`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition');
    let filename = `${reportKey.replace(/\//g, '-')}-${params.from_date}-to-${params.to_date}.csv`;
    if (disposition) {
      const match = /filename="?([^";\n]+)"?/.exec(disposition);
      if (match) filename = match[1].trim();
    }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  },
};
